<script lang="ts">
    import { computed, onActivated, reactive, ref } from 'vue';
    import vm, { tmp_uuidv4 } from '../Journeys.viewmodel';
    import { api } from '@cwd/lib/main';
    import { cancelFrame, requestFrame } from '@cwd/shared/utils';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import List from '@shared/components/List/List.vue';
    import { useRouter } from 'vue-router';
    import JourneyListItem from '../components/JourneyListItem.vue';
    import { type Journey } from '@journeys/journeys';
    import { getWriteAccessDisabledIds } from '@cwd/Acl/useAclAccess';

    interface JourneysTabData {
        list: { [key: string]: unknown | any };
        heading: string;
        intro: string;
        notification: string;
    }

    export const journeysOrderOptions = () => {
        return [
            {
                key: 'LastModified',
                display: $Resources.LastModifiedLabel,
            },
            {
                key: 'Title',
                display: $Resources.TitleLabel,
            },
            {
                key: 'Description',
                display: $Resources.DescriptionLabel,
            },
        ];
    };
</script>

<script lang="ts" setup>
    import { inject } from 'vue';
    import { type TagsProvide } from '@shared/components/Tags/TagsTypes';
    import { GetIssueSchedule, MoveToSent, SaveIssueSchedule } from '@journeys/Journey.access';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import Modal from '@shared/components/Modal.vue';
    import { type Issue } from '@journeys/journeys';
    import { FeatureService } from '@shared/directives/Feature.service';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);

    const tags = inject('tags') as TagsProvide;
    const router = useRouter();
    const { editor, startMenu } = vm;

    onActivated(async () => {
        const path = router.currentRoute.value.path;
        switchTab(path);
    });

    const journeysTabs = {
        drafts: { name: 'drafts', display: $Resources.JourneysDraftsTabLabel },
        active: { name: 'active', display: $Resources.JourneysActiveTabLabel },
        ended: { name: 'ended', display: $Resources.JourneysEndedTabLabel },
        templates: { name: 'templates', display: $Resources.JourneysTemplatesTabLabel },
    };

    const currentTab = ref('drafts');

    const tabData: JourneysTabData = reactive({
        list: {},
        heading: $Resources.JourneysDraftsHeading,
        intro: $Resources.JourneysDraftsIntroduction,
        notification: $Resources.JourneysNoDraftsLabel,
    });

    const filteredList = computed(() => {
        const res = Object.values(tabData.list);
        return res as Journey[];
    });

    const aclDisabledIds = computed(() =>
        getWriteAccessDisabledIds(
            filteredList.value,
            (journey) => journey.JourneyId,
            (journey) => journey.CurrentUserAccessLevel
        )
    );

    const switchTab = async (path: string) => {
        editor.state.busy = true;
        const statusName = path.toLowerCase().replace('journeys', '').replaceAll('/', '');
        currentTab.value = statusName;
        await fetchListings();
    };

const getEndpointForTab = () => {
        switch (currentTab.value) {
            case 'active': return '/journeys/Active';
            case 'ended': return '/journeys/Ended';
            case 'templates': return '/journeys/Templates';
            default: return '/journeys/Drafts';
        }
    };

    const fetchListings = async () => {
        const data = await api.get(getEndpointForTab()) as Journey[];
        editor.state.busy = false;
        setTabData(data ?? []);
    };

    const setTabData = (journeys: Journey[]) => {
        switch (currentTab.value) {
            case 'drafts':
                tabData.heading = $Resources.JourneysDraftsHeading;
                tabData.intro = $Resources.JourneysDraftsIntroduction;
                tabData.notification = $Resources.JourneysNoDraftsLabel;
                break;
            case 'active':
                tabData.heading = $Resources.JourneysActiveHeading;
                tabData.intro = $Resources.JourneysActiveIntroduction;
                tabData.notification = $Resources.JourneysNoActiveLabel;
                break;
            case 'ended':
                tabData.heading = $Resources.JourneysEndedHeading;
                tabData.intro = $Resources.JourneysEndedIntroduction;
                tabData.notification = $Resources.JourneysNoEndedLabel;
                break;
            case 'templates':
                tabData.heading = $Resources.JourneysTemplatesHeading;
                tabData.intro = $Resources.JourneysTemplatesIntroduction;
                tabData.notification = $Resources.JourneysNoTemplatesLabel;
                break;
        }

        Object.keys(tabData.list).forEach((key) => delete tabData.list[key]);
        journeys.forEach((journey) => {
            journey.data = JSON.parse(journey.GraphicalData);
            journey.data.Tags = journey.Tags;
            tabData.list[journey.JourneyId] = journey;
        });
    };

    let observerFrame = 0;

    const handleResizeChanges = (val: any) => {
        val.forEach((v: any) => {
            cancelFrame(observerFrame);
            observerFrame = requestFrame(() => {
                let el = v.target;
                let obj = resizeObserver[el.dataset.key],
                    { name, value } = obj;

                let res = v.contentRect[name] / value;
                obj.ref.value = res;
            });
        });
    };

    const resizeObserver: any = {
        observer: {},
        callback: handleResizeChanges,
    };

    const deleteSelectedItems = () => {
        let journeys = startMenu.selected.map((id) => {
            return { JourneyId: id };
        });

        api.delete(`/journeys`, journeys).then(() => {
            for (let { JourneyId } of journeys) {
                delete tabData.list[JourneyId!];
            }

            clearSelection();
        });
    };

    const clearSelection = () => {
        startMenu.selected = [];
    };

    const cleanJourney = () => {
        router.push('/Journeys/' + tmp_uuidv4() + '?clean=true');
    };

    const openTemplates = () => {
        router.push('/Journeys/new');
    };

    const openJourney = (journey: any) => {
        journey.data.JourneyId = journey.JourneyId;
        router.push('/Journeys/' + journey.data.JourneyId);
    };

    const modalOpen = ref(false);
    const abortedIssues = ref([] as { subject: string; issueId: string }[]);
    const journeyIssues = ref<Issue[]>([]);
    const reactivating = ref(false);

    function handleOpenAbortedIssuesModal(issues: Issue[]) {
        journeyIssues.value = issues;
        abortedIssues.value = issues.filter((i) => i.Status === 60)?.map((i) => ({ subject: i.Subject ?? i.Title, issueId: i.IssueId })) ?? [];
        modalOpen.value = true;
    }

    async function handleReActivateIssue(issueId: string) {
        if (reactivating.value || !featureAccess.hasFeature('Automation.Journeys')) return;
        reactivating.value = true;

        const currentSchedule = await GetIssueSchedule(issueId);

        if (currentSchedule.length === 0) {
            bsAlert({
                title: $Resources.ErrorReactivatingNoSchedule,
                content: $Resources.ErrorLabel,
                type: AlertTypes.Danger,
            });
            reactivating.value = false;
            return;
        }

        if (currentSchedule?.length > 1) {
            bsAlert({
                title: $Resources.ErrorReactivatingMultipleSchedules,
                content: $Resources.ErrorLabel,
                type: AlertTypes.Danger,
            });
            reactivating.value = false;
            return;
        }

        await MoveToSent(issueId);
        await SaveIssueSchedule(issueId, { ...currentSchedule[0], TaskStatus: 'Scheduled' });

        bsAlert({
            title: $Resources.GeneralSuccessAlertHeading,
            content: $Resources.SuccesReactivatingIssueContent,
        });

        abortedIssues.value = abortedIssues.value.filter((i) => i.issueId !== issueId);
        journeyIssues.value = journeyIssues.value.map((i) => (i.IssueId === issueId ? { ...i, Status: 70 } : i));
        const res = Object.values(tabData.list);
        const journeyId = res.find((j) => j.Issues.find((i: Issue) => i.IssueId === issueId)).JourneyId;
        tabData.list[journeyId].Issues = tabData.list[journeyId].Issues.map((i: Issue) => (i.IssueId === issueId ? { ...i, Status: 70 } : i));

        if (abortedIssues.value.length === 0) {
            modalOpen.value = false;
        }

        reactivating.value = false;
    }

    const updateRouter = (path: string) => {
        router.push('/Journeys/' + path);
    };
</script>

<template>
    <List
        itemId="JourneyId"
        :title="$Resources.JourneysHeader"
        :description="tabData.intro"
        :items="filteredList"
        :loading="editor.state.busy"
        :filterKeys="['Title', 'Description']"
        :orderOptions="journeysOrderOptions()"
        :checkedItems="startMenu.selected"
        displayAs="cards"
        :displayOptions="['default', 'cards']"
        :tabs="journeysTabs"
        :currentTab="currentTab"
        :disableCheckboxes="currentTab === 'active'"
        :disabledCheckboxes="aclDisabledIds"
        :tags="tags"
        :deleteConfirmation="true"
        @update:tab="updateRouter"
        @update:checkedItems="(newVal) => (startMenu.selected = newVal as string[])"
        @clearSelected="clearSelection"
        @deleteSelected="deleteSelectedItems">
        <template #toolbar>
            <BSDropdownButton menuClass="dropdown-menu-right" v-show="startMenu.selected.length === 0">
                <template #toggle>
                    <span class="glyphicon glyphicon-plus m-xxs-r"></span>
                    {{ $Resources.NewJourneyBtn }}
                </template>
                <template #content>
                    <li role="presentation" class="dropdown-header">{{ $Resources.NewJourneyBtn }}:</li>
                    <li>
                        <a href="javascript:void(0)" :class="editor.state.busy && 'pe-none'" @click="cleanJourney">
                            {{ $Resources.JourneysNewDraftBtnLabel }}
                        </a>
                    </li>
                    <li>
                        <a href="javascript:void(0)" :class="editor.state.busy && 'pe-none'" @click="openTemplates">
                            {{ $Resources.JourneysNewDraftFromTemplateBtnLabel }}
                        </a>
                    </li>
                </template>
            </BSDropdownButton>
        </template>
        <template #item="{ item, index }">
            <JourneyListItem
                :currentTab="currentTab"
                :journey="item"
                :index="index"
                :tags="tags"
                @journeyClicked="openJourney(item)"
                @reactivateAbortedIssuesBtnClicked="handleOpenAbortedIssuesModal"
                :hideStats="currentTab === 'drafts' || currentTab === 'templates'"></JourneyListItem>
        </template>
    </List>

    <Modal :title="$Resources.JourneyHasAbortedIssuesTitle" :show="modalOpen" @close="modalOpen = false">
        <template #content>
            <table class="table table-hover">
                <tbody class="td-align-middle">
                    <tr v-for="issue in abortedIssues" :key="issue.issueId">
                        <td>{{ issue.subject }}</td>
                        <td class="text-right">
                            <button
                                class="btn btn-default btn-sm"
                                :disabled="reactivating || !featureAccess.hasFeature('Automation.Journeys')"
                                type="button"
                                @click="handleReActivateIssue(issue.issueId)">
                                {{ $Resources.JourneyAbortedIssueReActivateBtn }}
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </template>
    </Modal>
</template>

<style lang="less"></style>
